import { Box, Paper, Title, Text, Button, Flex, Container, Anchor, ThemeIcon, Image, PinInput } from "@mantine/core";
import { IconMail, IconRefresh, IconArrowLeft } from "@tabler/icons-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useState, useEffect } from "react";
import trpc, { saveToken } from "../../shared/utils/trpc";
import { successNotification } from "../../shared/utils/successNotification";
import { handleTrpcError } from "../../shared/utils/trpc/handletrpcError.util";
import { useUser } from "@/context/UserContext";

function ResendEmailPage() {
  const navigate = useNavigate();
  const { refetchUser } = useUser();
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email") || "";

  const [loading, setLoading] = useState(false);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [cooldown, setCooldown] = useState(60);
  const [otpCode, setOtpCode] = useState("");

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;

    const timer = setInterval(() => {
      setCooldown((prev) => prev - 1);
    }, 1000);

    return () => clearInterval(timer);
  }, [cooldown]);

  const handleResend = async () => {
    if (!email || cooldown > 0) return;

    setLoading(true);
    try {
      await trpc.auth.resendVerification.mutate({ email });

      successNotification({
        title: "Verification code sent",
        message: "Please check your inbox for the latest code.",
      });

      setCooldown(60);
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!email || otpCode.trim().length !== 6) return;
    setVerifyLoading(true);
    try {
      const data = await trpc.auth.verifyEmail.mutate({
        email,
        otp: otpCode.trim(),
      });
      const token = data && typeof data === "object" && "token" in data ? (data as { token?: string }).token : undefined;
      if (!token) {
        throw new Error("Verification succeeded, but we could not start your session. Please log in.");
      }
      saveToken(token);
      await refetchUser();
      successNotification({
        title: "Email verified",
        message: "Your account has been verified successfully.",
      });
      navigate("/my-discovery", { replace: true });
    } catch (err) {
      handleTrpcError(err);
    } finally {
      setVerifyLoading(false);
    }
  };

  return (
    <Box mih="100vh" py={40}>
      <Container size="lg">
        {/* Header */}
        <Flex direction="column" align="center" gap={4} mb="xl">
          <Image src="/gtLogo.svg" alt="GTsetu" h={25} w={170} fit="contain" />
        </Flex>

        {/* Card */}
        <Paper
          shadow="sm"
          p={{ base: "lg", sm: "xl" }}
          radius="lg"
          maw={550}
          mx="auto"
          style={{ border: "1px solid #E5E7EB" }}
        >
          {/* Mail Icon */}
          <Flex justify="center" mb="lg">
            <ThemeIcon
              size={60}
              radius="xl"
              variant="light"
              color="primary"
              styles={{
                root: {
                  backgroundColor: "#DBEAFE",
                },
              }}
            >
              <IconMail size={32} color="#2563EB" />
            </ThemeIcon>
          </Flex>

          {/* Title */}
          <Title order={2} fz={{ base: 20, sm: 24 }} ta="center" mb="sm">
            Verify Your Email Address
          </Title>

          {/* Description */}
          <Text fz="sm" c="slate.6" ta="center" mb="xl">
            We've sent a verification code to your registered email address.
          </Text>

          {/* Email Box */}
          <Box bg="#F1F5F9" p="md" mb="md" style={{ borderRadius: "8px" }}>
            <Text fz="sm" ta="center" fw={500} c="#334155">
              {email}
            </Text>
            <Text fz="xs" c="slate.6" ta="center" mt="xs">
              Please check your inbox and use the latest code to continue.
            </Text>
          </Box>

          {/* Resend Button */}
          <Button
            fullWidth
            size="md"
            leftSection={<IconRefresh size={18} />}
            onClick={handleResend}
            loading={loading}
            disabled={cooldown > 0 || loading}
          >
            {cooldown > 0 ? `Resend Verification Code (${formatTime(cooldown)})` : "Resend Verification Code"}
          </Button>

          {/* Helper Text */}
          <Text fz="xs" c="slate.6" ta="center" mt="md">
            You can resend the code if you didn't receive it.
          </Text>

          <Text fz="sm" fw={600} ta="center" mt="xl" mb="sm">
            Enter OTP
          </Text>
          <Flex justify="center" mb="md">
            <PinInput type="number" length={6} value={otpCode} onChange={setOtpCode} oneTimeCode size="lg" />
          </Flex>
          <Button fullWidth size="md" onClick={handleVerify} loading={verifyLoading} disabled={otpCode.trim().length !== 6}>
            Verify and continue
          </Button>

          {/* Change Email Link */}
          <Flex justify="center" mt="md">
            <Anchor component={Link} to="/login" size="sm" c="#2563EB">
              <Flex align="center" gap={4}>
                <IconArrowLeft size={14} />
                Back to login
              </Flex>
            </Anchor>
          </Flex>
        </Paper>
      </Container>
    </Box>
  );
}

export default ResendEmailPage;
